import React, { useEffect, useMemo, useState } from "react";
import { getHRActivityLogs } from "../../services/activityLogService";
import { displayValue } from "../../utils/displayValue";

import "../../styles/hrPageShell.css";
const ACTIVITY_TYPES = [
  "Profile Saved",
  "Profile Submitted",
  "Profile Resubmitted",
  "Profile Verified",
  "Profile Returned",
  "Profile Rejected",
  "Profile Reopened",
  "Onboarding Started",
  "Onboarding Completed",
  "Employee Activated",
  "PerformanceAppraisal",
  "Appraisal Template Saved",
  "Appraisal Question Saved",
  "Appraisal Assignment Saved",
  "Self Appraisal Submitted",
  "Manager Review Submitted",
  "HR Review Submitted",
  "Appraisal Review Updated",
  "System Update"
];

const ACTIVITY_STATUSES = [
  "Success",
  "Pending",
  "Failed",
  "Correction Required",
  "Completed",
  "Cancelled",
  "Submitted",
  "Reviewed",
  "HRReviewed",
  "Approved",
  "Rejected"
];

const SOURCE_MODULES = [
  "Profile",
  "HR Profile Review",
  "Employee Master",
  "Onboarding",
  "PerformanceAppraisal",
  "Performance Appraisal",
  "Power Automate",
  "System"
];

function HRActivityLog() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [searchText, setSearchText] = useState("");
  const [activityTypeFilter, setActivityTypeFilter] = useState("");
  const [activityStatusFilter, setActivityStatusFilter] = useState("");
  const [sourceModuleFilter, setSourceModuleFilter] = useState("");

  const loadActivities = async () => {
    try {
      setLoading(true);
      setMessage("");

      const rows = await getHRActivityLogs({
        activityType: activityTypeFilter,
        activityStatus: activityStatusFilter,
        sourceModule: sourceModuleFilter
      });

      setActivities(rows || []);
    } catch (error) {
      console.error("Activity log load error:", error);
      setMessage(error.message || "Failed to load HR activity logs.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredActivities = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return activities.filter((activity) => {
      const matchesType = !activityTypeFilter || displayValue(activity.activityType) === activityTypeFilter;
      const matchesStatus = !activityStatusFilter || displayValue(activity.activityStatus) === activityStatusFilter;
      const matchesSource = !sourceModuleFilter || displayValue(activity.sourceModule) === sourceModuleFilter;

      const matchesSearch =
        !query ||
        displayValue(activity.activityId).toLowerCase().includes(query) ||
        displayValue(activity.employeeId).toLowerCase().includes(query) ||
        displayValue(activity.workEmail).toLowerCase().includes(query) ||
        displayValue(activity.activityBy).toLowerCase().includes(query) ||
        displayValue(activity.comments).toLowerCase().includes(query) ||
        displayValue(activity.oldStatus).toLowerCase().includes(query) ||
        displayValue(activity.newStatus).toLowerCase().includes(query);

      return matchesType && matchesStatus && matchesSource && matchesSearch;
    });
  }, [activities, searchText, activityTypeFilter, activityStatusFilter, sourceModuleFilter]);

 const formatDate = (value) => {
  const text = displayValue(value);
  if (!text || text === "-") return "-";

  const isDateOnly =
    /^\d{4}-\d{2}-\d{2}$/.test(text) ||
    /^\d{4}-\d{2}-\d{2}T00:00:00(?:\.000)?Z?$/.test(text);

  const date = new Date(text);

  if (Number.isNaN(date.getTime())) return text;

  if (isDateOnly) {
    return date.toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  }

  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true
  });
  };

  const getBadgeStyle = (status) => {
    if (status === "Completed" || status === "Success") {
      return { ...badgeStyle, color: "#166534", backgroundColor: "#dcfce7", borderColor: "#bbf7d0" };
    }
    if (status === "Failed" || status === "Correction Required") {
      return { ...badgeStyle, color: "#991b1b", backgroundColor: "#fee2e2", borderColor: "#fecaca" };
    }
    if (status === "Pending") {
      return { ...badgeStyle, color: "#92400e", backgroundColor: "#fef3c7", borderColor: "#fde68a" };
    }
    return { ...badgeStyle, color: "var(--fm-page-accent)", backgroundColor: "#e0f2fe", borderColor: "#bae6fd" };
  };

  const totalCount = activities.length;
  const successCount = activities.filter((activity) => ["Success", "Completed"].includes(displayValue(activity.activityStatus))).length;
  const correctionCount = activities.filter((activity) => ["Correction Required", "Failed"].includes(displayValue(activity.activityStatus))).length;

  return (
    <div className="fm-hr-page-shell" style={pageStyle}>
      <div className="fm-hr-hero" style={headerStyle}>
        <div>
          <h1 style={{ margin: 0 }}>HR Activity Log</h1>
          <p style={{ marginTop: "8px", color: "rgba(255,255,255,0.88)" }}>
            Track profile submission, verification, correction, and onboarding activities.
          </p>
        </div>
        <button type="button" style={refreshButtonStyle} onClick={loadActivities} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {message && (
        <div style={messageType === "error" ? errorMessageStyle : successMessageStyle}>{message}</div>
      )}

      <div style={kpiGridStyle}>
        <div style={kpiCardStyle}>
          <div style={kpiLabelStyle}>Total Activities</div>
          <div style={kpiValueStyle}>{totalCount}</div>
        </div>
        <div style={kpiCardStyle}>
          <div style={kpiLabelStyle}>Success / Completed</div>
          <div style={kpiValueStyle}>{successCount}</div>
        </div>
        <div style={kpiCardStyle}>
          <div style={kpiLabelStyle}>Failed / Correction</div>
          <div style={kpiValueStyle}>{correctionCount}</div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={filterGridStyle}>
          <input
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search employee ID, email, activity by, comments..."
            style={inputStyle}
          />
          <select value={activityTypeFilter} onChange={(event) => setActivityTypeFilter(event.target.value)} style={inputStyle}>
            <option value="">All Activity Types</option>
            {ACTIVITY_TYPES.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
          <select value={activityStatusFilter} onChange={(event) => setActivityStatusFilter(event.target.value)} style={inputStyle}>
            <option value="">All Statuses</option>
            {ACTIVITY_STATUSES.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
          <select value={sourceModuleFilter} onChange={(event) => setSourceModuleFilter(event.target.value)} style={inputStyle}>
            <option value="">All Modules</option>
            {SOURCE_MODULES.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </div>

        <div style={tableWrapperStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Activity Date</th>
                <th style={thStyle}>Employee</th>
                <th style={thStyle}>Activity</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>By</th>
                <th style={thStyle}>Module</th>
                <th style={thStyle}>Old Status</th>
                <th style={thStyle}>New Status</th>
                <th style={thStyle}>Comments</th>
              </tr>
            </thead>
            <tbody>
              {filteredActivities.length === 0 && (
                <tr>
                  <td style={emptyCellStyle} colSpan="9">
                    {loading ? "Loading activity logs..." : "No activity logs found."}
                  </td>
                </tr>
              )}

              {filteredActivities.map((activity, index) => {
                const activityStatus = displayValue(activity.activityStatus) || "-";
                const key = displayValue(activity.id) || displayValue(activity.ID) || displayValue(activity.activityId) || index;

                return (
                  <tr key={key}>
                    <td style={tdStyle}>{formatDate(activity.activityDate)}</td>
                    <td style={tdStyle}>
                      <strong>{displayValue(activity.employeeId) || "-"}</strong>
                      <div style={smallTextStyle}>{displayValue(activity.workEmail) || "-"}</div>
                    </td>
                    <td style={tdStyle}>{displayValue(activity.activityType) || "-"}</td>
                    <td style={tdStyle}><span style={getBadgeStyle(activityStatus)}>{activityStatus}</span></td>
                    <td style={tdStyle}>{displayValue(activity.activityBy) || "-"}</td>
                    <td style={tdStyle}>{displayValue(activity.sourceModule) || "-"}</td>
                    <td style={tdStyle}>{displayValue(activity.oldStatus) || "-"}</td>
                    <td style={tdStyle}>{displayValue(activity.newStatus) || "-"}</td>
                    <td style={tdStyle}>{displayValue(activity.comments) || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const pageStyle = { fontFamily: "Segoe UI, Calibri, Arial, sans-serif", padding: "18px", backgroundColor: "var(--fm-content)", minHeight: "100vh", color: "var(--fm-text)", boxSizing: "border-box", overflowX: "hidden" };
const headerStyle = { display: "flex", justifyContent: "space-between", gap: "18px", alignItems: "flex-start", flexWrap: "wrap", marginBottom: "16px", background: "var(--fm-hero-bg)", color: "#ffffff", borderRadius: "20px", padding: "22px 24px", boxShadow: "0 10px 28px rgba(0,80,150,0.16)" };
const cardStyle = { backgroundColor: "var(--fm-card)", border: "1px solid var(--fm-border)", borderRadius: "18px", padding: "16px", boxShadow: "0 3px 13px rgba(15,23,42,0.052)", minWidth: 0, overflow: "hidden" };
const kpiGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "11px", marginBottom: "14px", minWidth: 0 };
const kpiCardStyle = { ...cardStyle, padding: "13px" };
const kpiLabelStyle = { color: "var(--fm-muted)", fontSize: "11.5px", fontWeight: 750 };
const kpiValueStyle = { color: "var(--fm-page-accent)", fontSize: "26px", fontWeight: 800, marginTop: "4px", lineHeight: 1.15 };
const filterGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "14px", minWidth: 0 };
const inputStyle = { height: "40px", border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "8px 10px", fontSize: "13px", backgroundColor: "var(--fm-card)", width: "100%", minWidth: 0, boxSizing: "border-box" };
const textareaStyle = { width: "100%", border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px", fontSize: "13px", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" };
const tableWrapperStyle = { overflowX: "auto", maxWidth: "100%", border: "1px solid var(--fm-border)", borderRadius: "14px", WebkitOverflowScrolling: "touch" };
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: "12.5px", minWidth: "980px" };
const thStyle = { textAlign: "left", borderBottom: "1px solid var(--fm-border)", padding: "10px", backgroundColor: "#f8fafc", color: "var(--fm-text)", whiteSpace: "nowrap", fontWeight: 800 };
const tdStyle = { borderBottom: "1px solid var(--fm-border)", padding: "10px", color: "var(--fm-text)", verticalAlign: "top", whiteSpace: "nowrap" };
const emptyCellStyle = { padding: "18px", textAlign: "center", color: "var(--fm-muted)" };
const smallTextStyle = { color: "var(--fm-muted)", fontSize: "12px", marginTop: "3px" };
const badgeStyle = { display: "inline-flex", border: "1px solid", borderRadius: "999px", padding: "5px 10px", fontSize: "11.5px", fontWeight: 750, whiteSpace: "nowrap" };
const actionButtonGroupStyle = { display: "flex", gap: "8px", flexWrap: "wrap" };
const viewButtonStyle = { backgroundColor: "var(--fm-page-accent)", color: "#ffffff", border: "1px solid #0078d4", borderRadius: "10px", padding: "8px 12px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const approveButtonStyle = { backgroundColor: "#047857", color: "#ffffff", border: "1px solid #047857", borderRadius: "10px", padding: "8px 12px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const rejectButtonStyle = { backgroundColor: "#b91c1c", color: "#ffffff", border: "1px solid #b91c1c", borderRadius: "10px", padding: "8px 12px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const refreshButtonStyle = { backgroundColor: "#eef6ff", color: "var(--fm-page-accent)", border: "1px solid #bfdbfe", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const secondaryButtonStyle = { backgroundColor: "#eef6ff", color: "var(--fm-page-accent)", border: "1px solid #bfdbfe", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const modalOverlayStyle = { position: "fixed", inset: 0, backgroundColor: "rgba(15, 23, 42, 0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px", zIndex: 1000 };
const modalBoxStyle = { width: "100%", maxWidth: "560px", backgroundColor: "var(--fm-card)", borderRadius: "18px", padding: "22px", boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)" };
const largeModalBoxStyle = { width: "100%", maxWidth: "1000px", maxHeight: "88vh", overflowY: "auto", backgroundColor: "var(--fm-card)", borderRadius: "18px", padding: "22px", boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)" };
const modalHeaderStyle = { display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start", borderBottom: "1px solid var(--fm-border)", paddingBottom: "12px", marginBottom: "14px", flexWrap: "wrap" };
const modalActionsStyle = { display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px", flexWrap: "wrap" };
const labelStyle = { display: "block", marginBottom: "6px", fontWeight: 750, color: "var(--fm-text)" };
const successMessageStyle = { border: "1px solid #bbf7d0", backgroundColor: "#ecfdf5", color: "#047857", borderRadius: "13px", padding: "11px 13px", marginBottom: "14px", fontWeight: 700 };
const errorMessageStyle = { border: "1px solid #fecaca", backgroundColor: "#fef2f2", color: "#b91c1c", borderRadius: "13px", padding: "11px 13px", marginBottom: "14px", fontWeight: 700 };
const previewGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "12px", marginBottom: "16px" };
const previewFieldStyle = { border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px", backgroundColor: "#f8fafc" };
const previewLabelStyle = { color: "var(--fm-muted)", fontSize: "11.5px", fontWeight: 750, marginBottom: "4px" };
const previewValueStyle = { color: "var(--fm-text)", fontSize: "13px", wordBreak: "break-word" };
const previewSectionTitleStyle = { marginTop: "18px", marginBottom: "10px", color: "var(--fm-text)" };
const linkStyle = { color: "var(--fm-page-accent)", fontWeight: 750, textDecoration: "none" };
export default HRActivityLog;
